import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Button, Flex, Input, Modal, notification } from "antd";
import {
  addCandidateNote,
  deleteCandidateNote,
  editCandidate,
  updateCandidateNote,
} from "api/candidate.api";
import { ExtraSmallTitle, Title5 } from "components/Common/Typography/Titles";
import { useAppSelector } from "hooks/reduxHooks";
import { SvgIcons } from "Icon/SvgIcons";
import React, { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { candidateProfileRefresh, creditsRefreshChanger } from "store/slices/refresh.slice";
import { getDate, getDate2 } from "utils";
import { getCurrentDate } from "utils/utils";

const { confirm } = Modal;
const { TextArea } = Input;

interface CandidateNoteProps {
  candidateId: number;
  Notes?: Array<{
    id: number;
    user: {
      first_name: string;
      last_name: string;
      email: string;
    };
    custom_fields: {
      note: string;
      modified_date: string;
      is_edited?: boolean;
      edited_by?: string;
    };
  }>;
  setRefresh?: React.Dispatch<React.SetStateAction<boolean>>;
  loading?: boolean;
  editorName: string;
  setIsEditingNote?: React.Dispatch<React.SetStateAction<boolean>>;
  setError?: React.Dispatch<React.SetStateAction<string | null>>;
}

const CandidateNote: React.FC<CandidateNoteProps> = ({
  candidateId,
  Notes,
  setRefresh,
  loading,
  editorName,
  setIsEditingNote,
  setError,
}) => {
  const [note, setNote] = useState<string>("");
  const [updateNoteId, setUpdateNoteId] = useState<number | null>(null);
  const [expandedNotes, setExpandedNotes] = useState<Set<number>>(new Set());
  const dispatch = useDispatch();

  const authUser: any = useAppSelector((state) => state.user.user);

  const handleApiRequest = useCallback(
    async (action: "add" | "update") => {
      try {
        const candidateNotePayload = {
          note,
          edited_by: editorName,
          modified_date: new Date(),
          candidate: candidateId,
          is_edited: action === "add" ? false : true,
        };
        const apiCall =
          action === "add"
            ? addCandidateNote(candidateNotePayload as any)
            : updateCandidateNote(updateNoteId!, candidateNotePayload);

        await apiCall;
        if (setRefresh) setRefresh(true);
        notification.success({
          message: "Success",
          description: `Note ${action === "add" ? "added" : "updated"} successfully!`,
        });

        setNote("");
        setUpdateNoteId(null);
        dispatch(candidateProfileRefresh());
      } catch (error) {
        if (error instanceof Error) {
          notification.error({
            message: "Failed",
            description: error.message || "Something went wrong!",
          });
        } else {
          notification.error({
            message: "Failed",
            description: "An unknown error occurred",
          });
        }
      } finally {
        // if (setRefresh) setRefresh(false);
      }
    },
    [note, updateNoteId, candidateId, dispatch, setRefresh]
  );

  const showConfirm = useCallback(
    (id: number) => {
      confirm({
        title: "Are you sure you want to delete this note?",
        icon: <ExclamationCircleOutlined />,
        content: "",
        onOk: async () => {
          try {
            await deleteCandidateNote(id, candidateId);
            if (setRefresh) setRefresh(true);
            dispatch(candidateProfileRefresh());
            notification.success({ message: "Note deleted successfully" });
          } catch (error: any) {
            // Type assertion to 'any'
            notification.error({
              message: "Failed",
              description:
                error.response?.data?.message || "An error occurred while processing the note",
            });
          }
        },
      });
    },
    [candidateId, dispatch]
  );

  const submitNote = useCallback(() => {
    if (updateNoteId) {
      handleApiRequest("update");
      handleUpdateNote(true, note);
    } else {
      // handleApiRequest("add");
      handleUpdateNote(false, note);
    }
  }, [updateNoteId, handleApiRequest]);

  const handleEditNoteClick = useCallback((id: number, message: string) => {
    setUpdateNoteId(id);
    setNote(message);
  }, []);

  const handleUpdateNote = async (is_edited: boolean, note: string) => {
    const candidateNotePayload = {
      custom_fields: {
        note: note,
        edited_by: editorName,
        modified_date: new Date(),
        is_edited: is_edited,
      },
      candidate: candidateId,
    };
    try {
      const data = await editCandidate(candidateNotePayload);
      if (setRefresh) setRefresh(true);
      if (setIsEditingNote) setIsEditingNote(false);
    } catch (err) {
      if (setError) setError("Failed to update candidate description");
    } finally {
      // setRefresh(false);
      dispatch(creditsRefreshChanger());
    }
  };

  const toggleNoteExpansion = (noteId: number) => {
    const newExpandedNotes = new Set(expandedNotes);
    if (newExpandedNotes.has(noteId)) {
      newExpandedNotes.delete(noteId);
    } else {
      newExpandedNotes.add(noteId);
    }
    setExpandedNotes(newExpandedNotes);
  };

  const shouldTruncateNote = (noteText: string) => {
    // Rough estimation: 1 character ≈ 0.6em, 500px ≈ 83 characters per line, 4 lines ≈ 332 characters
    return noteText.length > 332;
  };

  return (
    <>
      <div className="py-2 px-[6px] border-b border-dashed">
        <Flex align="center" className="w-full">
          <div className="text-text-heading font-bold pr-2.5 text-base text-sm">My Notes:</div>
        </Flex>
      </div>
      <Flex gap={20} className="my-3 py-2 border-b border-dashed">
        <div className="py-[6px]">
          <ExtraSmallTitle text={getCurrentDate()} className="text-text-heading" />
        </div>
        <div className="flex-1 flex justify-end flex-col gap-[10px]">
          <TextArea
            id="candidateNotes"
            rows={4}
            placeholder="Add Note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <Button
            key="submit"
            loading={loading}
            disabled={!note.trim() || loading}
            onClick={submitNote}
            className="self-end relative font-semibold text-sm text-white py-2 px-3 bg-[#845dda] border border-[#7F56D9] flex gap-1 rounded-lg group"
          >
            {updateNoteId ? "Update Note" : "Save Your Note"}
          </Button>
        </div>
      </Flex>
      <Flex className="my-2" vertical>
        {Notes?.filter((item) => item?.custom_fields?.note?.trim()).map(
          ({ id, custom_fields, user }) => {
            const { note, edited_by, modified_date, is_edited } = custom_fields || {};
            const isExpanded = expandedNotes.has(id);
            const needsTruncation = shouldTruncateNote(note);
            const displayNote =
              needsTruncation && !isExpanded ? note.substring(0, 332) + "..." : note;

            return (
              <Flex gap={10} key={id} className="border-b py-3 last:border-0">
                <div>
                  <ExtraSmallTitle
                    text={getDate2(modified_date)}
                    className="text-xs text-text-heading"
                  />
                </div>
                <div className="flex-1">
                  <div
                    className="text-text-tertiary mb-0 text-sm font-normal whitespace-pre-wrap"
                    style={{
                      maxHeight: isExpanded ? "none" : "500px",
                      overflow: isExpanded ? "visible" : "hidden",
                    }}
                  >
                    <b>{edited_by || `${user.first_name} ${user.last_name}`}:</b> {displayNote}
                    {is_edited && (
                      <span className="text-xs italic text-gray-400 ml-2">(edited)</span>
                    )}
                  </div>
                  {needsTruncation && (
                    <button
                      onClick={() => toggleNoteExpansion(id)}
                      className="text-[#845dda] hover:text-[#7F56D9] underline text-sm font-medium mt-1 cursor-pointer bg-transparent border-none p-0"
                    >
                      {isExpanded ? "Read LESS" : "Read MORE"}
                    </button>
                  )}
                </div>
                {user?.email === authUser?.email && (
                  <div className="min-w-[26px] flex gap-2 items-center">
                    <span className="cursor-pointer" onClick={() => showConfirm(id)}>
                      {SvgIcons.deleteSvg}
                    </span>
                    {/* <span className="cursor-pointer" onClick={() => handleEditNoteClick(id, note)}>
                    {SvgIcons.editSvg}
                  </span> */}
                  </div>
                )}
              </Flex>
            );
          }
        )}
      </Flex>
    </>
  );
};

export default React.memo(CandidateNote);
